import React, { useState } from 'react';
import style from './index.module.css';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseApp } from '../../utils/firebase';

const ContactForm = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    
    const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('Submitting form with data:', formData);
        setStatus('loading');

        try {
            const db = getFirestore();
            console.log('Firestore initialized successfully');
            
            const docRef = await addDoc(collection(db, 'contact_inquiries'), {
                ...formData,
                timestamp: serverTimestamp()
            });
            
            console.log('Document written with ID: ', docRef.id);
            setStatus('success');
            setFormData({ name: '', email: '', phone: '', message: '' });
        } catch (error) {
            console.error('--- FIREBASE ERROR START ---');
            console.error('Error Code:', error.code);
            console.error('Error Message:', error.message);
            console.error('Full Error Object:', error);
            console.error('--- FIREBASE ERROR END ---');
            setStatus('error');
        }
    };

    if (status === 'success') {
        return (
            <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={style.successMessage}
            >
                <CheckCircleIcon sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
                <h2>Thank You!</h2>
                <p>Your message has been sent successfully. We will get back to you shortly.</p>
                <Button 
                    variant="contained" 
                    onClick={() => setStatus('idle')}
                    sx={{ mt: 3 }}
                >
                    Send Another Message
                </Button>
            </motion.div>
        );
    }

    return (
        <form className={style.form} onSubmit={handleSubmit}>
            <TextField
                fullWidth
                label="Your Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                variant="outlined"
                margin="normal"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                    }
                }}
            />
            <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                variant="outlined"
                margin="normal"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                    }
                }}
            />
            <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                variant="outlined"
                margin="normal"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                    }
                }}
            />
            <TextField
                fullWidth
                label="Your Message"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                required
                variant="outlined"
                margin="normal"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                    }
                }}
            />
            
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={status === 'loading'}
                    endIcon={<SendIcon />}
                    sx={{
                        mt: 4,
                        py: 1.5,
                        borderRadius: 3,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                    }}
                >
                    {status === 'loading' ? 'Sending...' : 'Send Inquiry'}
                </Button>
            </motion.div>
            
            {status === 'error' && (
                <p className={style.errorMessage}>Something went wrong. Please try again or call us directly.</p>
            )}
        </form>
    );
};

export default ContactForm;
